import { AppModel, getHomeUrl, reportError } from "app/client/models/AppModel";
import { urlState } from "app/client/models/gristUrlState";
import { IFormData } from "app/client/ui/BillingFormTypes";
import { ChangeReason, PlanContentModel } from "app/client/ui/PlanCard";
import { buildUpgradeModal } from "app/client/ui/ProductUpgrades";
import { confirmModal } from "app/client/ui2018/modals";
import { BillingAPI, BillingAPIImpl, BillingSubPage,
  BillingTask, IBillingPlan, IBillingSubscription, PlanSelection } from "app/common/BillingAPI";
import { FullUser } from "app/common/LoginSessionAPI";

import { bundleChanges, Computed, Disposable, Observable } from "grainjs";

export interface BillingModel {
  readonly error: Observable<string | null>;
  // Plans available to the user.
  readonly plans: Observable<IBillingPlan[]>;
  // Client-friendly version of the IBillingSubscription fetched from the server.
  // See ISubscriptionModel for details.
  readonly subscription: Observable<ISubscriptionModel | undefined>;

  readonly currentSubpage: Computed<BillingSubPage | undefined>;
  // The billingTask query param of the url - indicates the current operation, if any.
  // See BillingTask in BillingAPI for details.
  readonly currentTask: Computed<BillingTask | undefined>;
  // The planId of the plan to which the user is in process of signing up.
  readonly signupPlanId: Computed<string | undefined>;
  // The plan to which the user is in process of signing up.
  readonly signupPlan: Computed<IBillingPlan | undefined>;
  // Indicates whether the request for billing account information fails with unauthorized.
  // Initialized to false until the request is made.
  readonly isUnauthorized: Observable<boolean>;

  reportBlockingError(this: void, err: Error): void;

  // Fetch billing account managers.
  fetchManagers(): Promise<FullUser[]>;
  // Add billing account manager.
  addManager(email: string): Promise<void>;
  // Remove billing account manager.
  removeManager(email: string): Promise<void>;
  // Returns a boolean indicating if the org domain string is available.
  isDomainAvailable(domain: string): Promise<boolean>;
  // Fetches subscription data associated with the given org, if the pages are associated with an
  // org and the user is a plan manager. Otherwise, fetches available plans only.
  fetchData(forceReload?: boolean): Promise<void>;
  // Triggered when submit is clicked on the payment page. Performs the API billing account
  // management call based on currentTask, signupPlan and whether an address/tokenId was submitted.
  submitPaymentPage(formData?: IFormData): Promise<void>;
  // Cancels current subscription.
  cancelCurrentPlan(): Promise<void>;
  // Retrieves customer portal session URL.
  getCustomerPortalUrl(): string;
  // Renews plan (either by opening customer portal or creating Stripe Checkout session)
  renewPlan(plan: PlanSelection): Promise<{ checkoutUrl: string }>;
  // Upgrades plan (or downgrades, if the new plan is cheaper/free)
  changePlan(plan: PlanSelection): Promise<void>;
  // Upgrades assistant plan.
  upgradeAssistantPlan(tier: number): Promise<void>;
  /** Assigns an existing subscription to this org */
  attachSubscription(subscriptionIdOrCustomerId: string): Promise<void>;
  /**
   * Attaches a payment link to this org. If the org doesn't have a subscription (doesn't have customerId or
   * customer doesn't have active subscription in Stripe), it will show a payment link and allow user to enroll
   * in custom plan read from that link. It will also change the product from the one in the link and make
   * the account in bad standing (awaiting for payment).
   */
  attachPayment(paymentLink: string): Promise<void>;
  /**
   * Changes product of this org. If the org doesn't have a subscription.
   */
  changeProduct(productId: string): Promise<void>;
  /**
   * Makes a payment from the attached payment link, moving the org to the new product.
   */
  makePayment(): Promise<void>;
  /**
   * Cancels the plan change that was in progress (confirms first).
   */
  cancelPlanChange(): Promise<void>;
  /**
   * Removes the scheduled cancellation.
   */
  dontCancelPlan(endDate: string): Promise<void>;
  /**
   * Updates the custom logo for the org.
   */
  updateCustomLogo(url: string): Promise<void>;
}

export interface ISubscriptionModel extends IBillingSubscription {
  // The active plan.
  activePlan: IBillingPlan | null;
  // The upcoming plan, or null if the current plan is not set to end.
  upcomingPlan: IBillingPlan | null;
}

/**
 * Creates the model for the BillingPage. See app/client/ui/BillingPage for details.
 */
export class BillingModelImpl extends Disposable implements BillingModel {
  public readonly error = Observable.create<string | null>(this, null);
  // Plans available to the user.
  public readonly plans: Observable<IBillingPlan[]> = Observable.create(this, []);
  // Client-friendly version of the IBillingSubscription fetched from the server.
  // See ISubscriptionModel for details.
  public readonly subscription: Observable<ISubscriptionModel | undefined> = Observable.create(this, undefined);

  public readonly currentSubpage: Computed<BillingSubPage | undefined> =
    Computed.create(this, urlState().state, (use, s) => s.billing === "billing" ? undefined : s.billing);

  // The billingTask query param of the url - indicates the current operation, if any.
  // See BillingTask in BillingAPI for details.
  public readonly currentTask: Computed<BillingTask | undefined> =
    Computed.create(this, urlState().state, (use, s) => s.params?.billingTask);

  // The planId of the plan to which the user is in process of signing up.
  public readonly signupPlanId: Computed<string | undefined> =
    Computed.create(this, urlState().state, (use, s) => s.params?.billingPlan);

  // The plan to which the user is in process of signing up.
  public readonly signupPlan: Computed<IBillingPlan | undefined> =
    Computed.create(this, this.plans, this.signupPlanId, (use, plans, pid) => plans.find(_p => _p.id === pid));

  // Indicates whether the request for billing account information fails with unauthorized.
  // Initialized to false until the request is made.
  public readonly isUnauthorized: Observable<boolean> = Observable.create(this, false);

  public readonly reportBlockingError = this._reportBlockingError.bind(this);

  private readonly _billingAPI: BillingAPI = new BillingAPIImpl(getHomeUrl());

  constructor(public appModel: AppModel) {
    super();
  }

  public canOverrideBilling(): boolean {
    return this.appModel.isInstallAdmin() || this.appModel.isSupport();
  }

  // Fetch billing account managers to initialize the dom.
  public async fetchManagers(): Promise<FullUser[]> {
    const billingAccount = await this._billingAPI.getBillingAccount();
    return billingAccount.managers;
  }

  public async addManager(email: string): Promise<void> {
    await this._billingAPI.updateBillingManagers({
      users: { [email]: "managers" },
    });
  }

  public async removeManager(email: string): Promise<void> {
    await this._billingAPI.updateBillingManagers({
      users: { [email]: null },
    });
  }

  public isDomainAvailable(domain: string): Promise<boolean> {
    return this._billingAPI.isDomainAvailable(domain);
  }

  public getCustomerPortalUrl() {
    return this._billingAPI.customerPortal();
  }

  public renewPlan(plan: PlanSelection) {
    return this._billingAPI.renewPlan(plan);
  }

  public async changePlan(plan: PlanSelection): Promise<void> {
    await this._billingAPI.changePlan(plan);
  }

  public async cancelCurrentPlan() {
    await this._billingAPI.cancelCurrentPlan();
  }

  public async upgradeAssistantPlan(value: number) {
    await this._billingAPI.updateAssistantPlan(value);
  }

  public async submitPaymentPage(formData: IFormData = {}): Promise<void> {
    const task = this.currentTask.get();
    // TODO: The server should prevent most of the errors in this function from occurring by
    // redirecting improper urls.
    try {
      if ((task === "signUpLite" || task === "updateDomain") && formData.settings) {
        // All that can change here is company name, and domain.
        const org = this.appModel.currentOrg;
        if (!org) {
          throw new Error("BillingPage _submit error: no org");
        }
        const name = formData.settings.name;
        const domain = formData.settings.domain;
        const logo = formData.settings.customLogoUrl;
        const isChanged = (x: any, y: any) => (x || "") !== (y || "");
        const newDomain = isChanged(domain, org.domain);
        const newName = isChanged(name, org.name);
        const newLogo = isChanged(logo, org.orgPrefs?.customLogoUrl);
        const newSettings = newName || newDomain || newLogo;
        if (newSettings) {
          await this._billingAPI.updateSettings(formData.settings);
        }
        // If the domain has changed, should redirect page.
        if (newDomain && domain) {
          window.location.assign(urlState().makeUrl({ org: domain, billing: "billing", params: undefined }));
          return;
        }
        // If there is an org update, re-initialize the org in the client.
        if (newSettings) { this.appModel.topAppModel.initialize(); }
      } else {
        throw new Error("BillingPage _submit error: no task in progress");
      }
      // Show the billing summary page after submission
      await urlState().pushUrl({ billing: "billing", params: undefined });
    } catch (err) {
      // TODO: These errors may need to be reported differently since they're not user-friendly
      reportError(err);
      throw err;
    }
  }

  public async updateCustomLogo(customLogoUrl: string) {
    await this._billingAPI.updateSettings({
      customLogoUrl,
    });
    this.appModel.topAppModel.initialize();
    await urlState().pushUrl({ billing: "billing", params: undefined });
  }

  // If forceReload is set, re-fetches and updates already fetched data.
  public async fetchData(forceReload: boolean = false): Promise<void> {
    // If these are billing settings pages for an existing org, fetch the subscription data.
    await this._fetchSubscription(forceReload);
  }

  /**
   * Read existing plans from the server and convert them to PlanContentModel.
   */
  public async readPlans(owner: Disposable, plan?: PlanSelection, reason?: ChangeReason) {
    const stripePlans = await this._billingAPI.getPlans(plan);

    // We will share user counts per plan intervals.

    const map = new Map<string, Observable<number | undefined>>();
    stripePlans.forEach((p) => {
      if (!map.has(p.product)) {
        const miniumUnit = p.metadata.minimumUnits ?? 1;
        map.set(p.product, Observable.create<number | undefined>(owner, miniumUnit));
      }
    });

    return stripePlans.map((bp) => {
      const p = new PlanContentModel({
        allPlans: stripePlans,
        data: bp,
        reason: reason || "create",
      });
      owner.autoDispose(p);
      return p;
    });
  }

  public async attachSubscription(subscriptionIdOrCustomerId: string): Promise<void> {
    await this._billingAPI.attachSubscription(subscriptionIdOrCustomerId);
    await this.fetchData(true);
  }

  public async attachPayment(paymentLink: string): Promise<void> {
    await this._billingAPI.attachPayment(paymentLink);
    await this.fetchData(true);
  }

  public async changeProduct(productId: string): Promise<void> {
    await this._billingAPI.changeProduct(productId);
    await this.fetchData(true);
  }

  public async makePayment(): Promise<void> {
    const result = await this._billingAPI.getPaymentLink();
    if ("checkoutUrl" in result) {
      window.location.href = result.checkoutUrl;
    } else {
      buildUpgradeModal(this, {
        appModel: this.appModel,
        pickPlan: {
          priceId: result.priceId,
        },
        summary: result,
      }).catch(reportError);
    }
  }

  public async cancelPlanChange(): Promise<void> {
    confirmModal("Confirm plan change", "Keep current plan", async () => {
      await this._billingAPI.cancelPlanChange();
      this.fetchData(true).catch(reportError);
    }, {
      explanation: "You are about to cancel the plan change. Do you want to keep the current plan?",
    });
  }

  public async dontCancelPlan(endDate: string): Promise<void> {
    confirmModal("Keep subscription", "Keep plan", async () => {
      await this._billingAPI.dontCancelPlan();
      this.fetchData(true).catch(reportError);
    }, {
      explanation: `Your current plan will no longer end on ${endDate}, but will continue to renew.`,
    });
  }

  private _reportBlockingError(err: Error) {
    // TODO billing pages don't instantiate notifications UI (they probably should).
    reportError(err);
    const details = (err as any).details;
    const message = (details?.userError) || err.message;
    this.error.set(message);
  }

  private async _fetchSubscription(forceReload: boolean = false): Promise<void> {
    if (forceReload || this.subscription.get() === undefined) {
      try {
        // Unset while fetching for forceReload, so that the user (and tests) can tell that a
        // fetch is pending.
        this.subscription.set(undefined);
        const sub = await this._billingAPI.getSubscription();
        bundleChanges(() => {
          this.plans.set(sub.plans);
          const subModel: ISubscriptionModel = {
            activePlan: sub.plans[sub.planIndex],
            upcomingPlan: sub.upcomingPlanIndex !== sub.planIndex ? sub.plans[sub.upcomingPlanIndex] : null,
            ...sub,
          };
          this.subscription.set(subModel);
          // Clear the fetch errors on success.
          this.isUnauthorized.set(false);
          this.error.set(null);
        });
      } catch (e) {
        this.error.set(`Could not fetch subscription info: ${e.details?.userError || e.message}`);
        if (e.status === 401 || e.status === 403) { this.isUnauthorized.set(true); }
        throw e;
      }
    }
  }
}

export function centsToUSD(cents?: number) {
  if (cents === undefined) { return; }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Math.floor(cents) / 100);
}
